import { InjectionToken } from '@angular/core';

/**
 * Per-app knobs for the shared auth stack. Provided once via provideAuth() in app.config.
 * argus signs salon staff in, juno signs Friday Tech employees in; the flow is the same,
 * only these values differ.
 */
export interface AuthConfig {
  /** API origin. '' in local serve (dev proxy). Requests outside it never get the token header. */
  baseUrl: string;
  /** identity sign-in route, e.g. '/identity/company/v1/auth/sign-in'. */
  signInPath: string;
  /** identity refresh-token route. */
  refreshPath: string;
  /**
   * Which key of a sign-in or refresh response holds the user object. identity really does
   * answer two shapes: juno's employee-v3 session `{ employee }`, argus's staff-v1 sign-in
   * `{ staff }`. The sign-in page saves it on sign-in; TokenCachingInterceptor saves it again on
   * every refresh, so what identity puts on it later (a promotion, `effectiveRoles` since
   * 2026-09-07) reaches the stored user without a new sign-in.
   */
  userField: 'employee' | 'staff';
  /** localStorage key holding the signed-in user object. */
  userStorageKey: string;
  /** refresh the access token this many minutes before it expires. */
  refreshTokenExpMinutes: number;
  /** route the user lands on after a forced logout. */
  loginPath: string;
  /** hard-reload after a forced logout. argus does, juno does not. */
  reloadOnLogout: boolean;
  /**
   * identity's employee `me` base path, e.g. '/identity/company/v3/me'. Optional: an app that
   * leaves it unset gets no My account entry point at all (docs/ui/my-account.md decision 11).
   * Only meaningful on a v3 employee token; a v1 token's employee id is not in the v3 store.
   */
  mePath?: string;
}

export const AUTH_CONFIG = new InjectionToken<AuthConfig>('AUTH_CONFIG');

export const AUTH_ACCESS_TOKEN_KEY = 'AUTH_ACCESS_TOKEN';
export const AUTH_REFRESH_TOKEN_KEY = 'AUTH_REFRESH_TOKEN';

/** One token as identity returns it. expiresAt is seconds on the wire, ms once cached (see TokenCachingInterceptor). */
export interface AuthToken {
  token: string;
  expiresAt: number;
  type?: string;
}

/** Sign-in and refresh both return this, plus the user object under `AuthConfig.userField` (argus `staff`, juno `employee`). */
export interface AuthResp {
  accessToken: AuthToken;
  refreshToken: AuthToken;
}
